import { useState, useEffect, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { Separator } from "../components/ui/separator";
import { ScrollArea } from "../components/ui/scroll-area";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { Checkbox } from "../components/ui/checkbox";
import { CrackedLogo } from "../components/CrackedLogo";
import { 
  Calendar, 
  Clock, 
  Link2, 
  Copy, 
  Check, 
  LogOut, 
  Settings,
  ChevronRight,
  Video,
  Trash2,
  Plus,
  X,
  Copy as CopyIcon
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const FRONTEND_URL = window.location.origin;

const DAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

function CopyToPopover({ sourceDayIndex, onCopy }) {
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState([]);

  const toggle = (idx) => {
    setSelected(prev =>
      prev.includes(idx) ? prev.filter(i => i !== idx) : [...prev, idx]
    );
  };

  const apply = () => {
    if (selected.length === 0) return;
    onCopy(sourceDayIndex, selected);
    setSelected([]);
    setOpen(false);
  };

  // Weekday/weekend quick-select helpers (excluding the source day)
  const weekdayIdxs = [0, 1, 2, 3, 4].filter(i => i !== sourceDayIndex);
  const weekendIdxs = [5, 6].filter(i => i !== sourceDayIndex);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="text-cracked-deep-teal/70 hover:bg-cracked-light-teal/10"
          data-testid={`day-${sourceDayIndex}-copy-to`}
        >
          <CopyIcon className="w-3.5 h-3.5 mr-1" />
          Copy to...
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-64" align="end">
        <div className="space-y-3">
          <div>
            <p className="text-sm font-medium text-cracked-deep-teal">
              Copy {DAYS[sourceDayIndex]}'s schedule to
            </p>
            <p className="text-xs text-cracked-deep-teal/50 mt-0.5">
              Selected days will be overwritten
            </p>
          </div>
          <div className="flex gap-2 text-xs">
            <button
              type="button"
              onClick={() => setSelected(weekdayIdxs)}
              className="text-cracked-light-teal hover:underline"
              data-testid={`day-${sourceDayIndex}-copy-weekdays`}
            >
              Weekdays
            </button>
            <span className="text-cracked-deep-teal/30">·</span>
            <button
              type="button"
              onClick={() => setSelected(weekendIdxs)}
              className="text-cracked-light-teal hover:underline"
              data-testid={`day-${sourceDayIndex}-copy-weekends`}
            >
              Weekend
            </button>
            <span className="text-cracked-deep-teal/30">·</span>
            <button
              type="button"
              onClick={() => setSelected([])}
              className="text-cracked-deep-teal/50 hover:underline"
            >
              Clear
            </button>
          </div>
          <div className="space-y-1.5">
            {DAYS.map((day, idx) => {
              if (idx === sourceDayIndex) return null;
              return (
                <label
                  key={day}
                  className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-slate-50 cursor-pointer"
                >
                  <Checkbox
                    checked={selected.includes(idx)}
                    onCheckedChange={() => toggle(idx)}
                    data-testid={`day-${sourceDayIndex}-copy-target-${idx}`}
                  />
                  <span className="text-sm text-cracked-deep-teal">{day}</span>
                </label>
              );
            })}
          </div>
          <Button
            type="button"
            onClick={apply}
            disabled={selected.length === 0}
            className="w-full bg-cracked-light-teal hover:bg-cracked-deep-teal text-white"
            size="sm"
            data-testid={`day-${sourceDayIndex}-copy-apply`}
          >
            Apply to {selected.length} {selected.length === 1 ? 'day' : 'days'}
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}

export default function Dashboard() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [user, setUser] = useState(null);
  const [availability, setAvailability] = useState({ slot_duration: 10, slots: [] });
  const [bookings, setBookings] = useState([]);
  const [copied, setCopied] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('availability');

  const fetchData = useCallback(async (userId) => {
    try {
      const [userRes, availRes, bookingsRes] = await Promise.all([
        axios.get(`${API}/users/me?user_id=${userId}`),
        axios.get(`${API}/availability/${userId}`),
        axios.get(`${API}/bookings/user/${userId}`)
      ]);
      
      setUser(userRes.data);
      setAvailability(availRes.data);
      setBookings(bookingsRes.data);
    } catch (error) {
      toast.error("Failed to load dashboard data");
      navigate('/');
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    let userId = searchParams.get('user_id');
    if (userId) {
      localStorage.setItem('cracked_user_id', userId);
    } else {
      userId = localStorage.getItem('cracked_user_id');
    }

    if (!userId) {
      navigate('/');
      return;
    }

    fetchData(userId);
  }, [searchParams, navigate, fetchData]);

  const handleLogout = async () => {
    try {
      const userId = localStorage.getItem('cracked_user_id');
      await axios.get(`${API}/auth/logout?user_id=${userId}`);
      localStorage.removeItem('cracked_user_id');
      navigate('/');
    } catch (error) {
      localStorage.removeItem('cracked_user_id');
      navigate('/');
    }
  };

  const copyBookingLink = () => {
    if (user?.booking_link) {
      const link = `${FRONTEND_URL}/book/${user.booking_link}`;
      navigator.clipboard.writeText(link);
      setCopied(true);
      toast.success("Booking link copied!");
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const toggleDayAvailability = (dayIndex) => {
    const hasAny = availability.slots.some(s => s.day_of_week === dayIndex);

    if (hasAny) {
      // Remove all ranges for this day
      setAvailability(prev => ({
        ...prev,
        slots: prev.slots.filter(s => s.day_of_week !== dayIndex)
      }));
    } else {
      // Add day with one default range
      setAvailability(prev => ({
        ...prev,
        slots: [...prev.slots, { day_of_week: dayIndex, start_time: '09:00', end_time: '17:00' }]
      }));
    }
  };

  const addRangeToDay = (dayIndex) => {
    // Pick sensible defaults: start after the latest existing end_time
    const existing = availability.slots
      .filter(s => s.day_of_week === dayIndex)
      .sort((a, b) => a.start_time.localeCompare(b.start_time));
    const lastEnd = existing.length > 0 ? existing[existing.length - 1].end_time : '14:00';
    // Default new range = lastEnd → lastEnd + 1 hour (clamped to 23:00)
    const [h, m] = lastEnd.split(':').map(Number);
    const newStartH = Math.min(h, 22);
    const newEndH = Math.min(h + 1, 23);
    const pad = (n) => String(n).padStart(2, '0');
    setAvailability(prev => ({
      ...prev,
      slots: [
        ...prev.slots,
        {
          day_of_week: dayIndex,
          start_time: `${pad(newStartH)}:${pad(m)}`,
          end_time: `${pad(newEndH)}:${pad(m)}`,
        }
      ]
    }));
  };

  const removeRange = (dayIndex, rangeIndex) => {
    // rangeIndex is the index *within that day's ranges*
    const dayRanges = availability.slots
      .map((s, originalIdx) => ({ s, originalIdx }))
      .filter(({ s }) => s.day_of_week === dayIndex);
    const target = dayRanges[rangeIndex];
    if (!target) return;
    setAvailability(prev => ({
      ...prev,
      slots: prev.slots.filter((_, i) => i !== target.originalIdx)
    }));
  };

  const updateRange = (dayIndex, rangeIndex, field, value) => {
    const dayRanges = availability.slots
      .map((s, originalIdx) => ({ s, originalIdx }))
      .filter(({ s }) => s.day_of_week === dayIndex);
    const target = dayRanges[rangeIndex];
    if (!target) return;
    setAvailability(prev => ({
      ...prev,
      slots: prev.slots.map((s, i) =>
        i === target.originalIdx ? { ...s, [field]: value } : s
      )
    }));
  };

  const copyDayTo = (sourceDayIndex, targetDayIndices) => {
    // Copy all ranges from sourceDayIndex to each target day, overwriting
    // any existing ranges on those target days.
    const sourceRanges = availability.slots.filter(s => s.day_of_week === sourceDayIndex);
    if (sourceRanges.length === 0 || targetDayIndices.length === 0) return;

    setAvailability(prev => {
      // Remove all existing ranges for target days
      const filtered = prev.slots.filter(s => !targetDayIndices.includes(s.day_of_week));
      // Append copies of source ranges for each target day
      const additions = targetDayIndices.flatMap(targetDay =>
        sourceRanges.map(r => ({
          day_of_week: targetDay,
          start_time: r.start_time,
          end_time: r.end_time,
        }))
      );
      return { ...prev, slots: [...filtered, ...additions] };
    });

    const dayNames = targetDayIndices.map(i => DAYS[i]).join(', ');
    toast.success(`Copied ${DAYS[sourceDayIndex]}'s schedule to ${dayNames}`);
  };

  const saveAvailability = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/availability/${user.id}`, availability);
      toast.success("Availability saved!");
    } catch (error) {
      toast.error("Failed to save availability");
    } finally {
      setSaving(false);
    }
  };

  const cancelBooking = async (bookingId) => {
    try {
      await axios.delete(`${API}/bookings/${bookingId}?user_id=${user.id}`);
      setBookings(prev => prev.filter(b => b.id !== bookingId));
      toast.success("Booking cancelled");
    } catch (error) {
      toast.error("Failed to cancel booking");
    }
  };

  const formatDateTime = (isoString) => {
    const date = new Date(isoString);
    return {
      date: date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' }),
      time: date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })
    };
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-cracked-deep-teal text-white px-6 py-4">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <CrackedLogo inverted />
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-3">
              {user?.picture && (
                <img src={user.picture} alt={user.name} className="w-8 h-8 rounded-full" />
              )}
              <span className="text-sm font-medium hidden sm:block">{user?.name}</span>
            </div>
            <Button 
              variant="ghost" 
              size="sm"
              className="text-white hover:bg-white/10"
              onClick={handleLogout}
              data-testid="logout-btn"
            >
              <LogOut className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-8">
        {/* Booking Link Card */}
        <Card className="mb-8 border-2 border-cracked-light-teal/20 bg-gradient-to-r from-cracked-light-teal/5 to-white">
          <CardContent className="p-6">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-cracked-light-teal/10 flex items-center justify-center">
                  <Link2 className="w-6 h-6 text-cracked-light-teal" />
                </div>
                <div>
                  <h2 className="font-semibold text-cracked-deep-teal">Your Booking Link</h2>
                  <p className="text-sm text-cracked-deep-teal/60">Share this link with counsellors to book your calendar</p>
                </div>
              </div>
              <Button
                onClick={copyBookingLink}
                className="bg-cracked-light-teal hover:bg-cracked-deep-teal text-white"
                data-testid="copy-booking-link-btn"
              >
                {copied ? <Check className="w-4 h-4 mr-2" /> : <Copy className="w-4 h-4 mr-2" />}
                {copied ? 'Copied!' : 'Copy Link'}
              </Button>
            </div>
            <div className="mt-4 p-3 bg-white rounded-lg border border-cracked-deep-teal/10 font-mono text-sm text-cracked-deep-teal/70 break-all">
              {FRONTEND_URL}/book/{user?.booking_link}
            </div>
          </CardContent>
        </Card>

        {/* Tabs */}
        <div className="flex gap-2 mb-6">
          <Button
            variant={activeTab === 'availability' ? 'default' : 'outline'}
            onClick={() => setActiveTab('availability')}
            className={activeTab === 'availability' ? 'bg-cracked-light-teal hover:bg-cracked-deep-teal' : ''}
            data-testid="availability-tab"
          >
            <Settings className="w-4 h-4 mr-2" />
            Availability
          </Button>
          <Button
            variant={activeTab === 'bookings' ? 'default' : 'outline'}
            onClick={() => setActiveTab('bookings')}
            className={activeTab === 'bookings' ? 'bg-cracked-light-teal hover:bg-cracked-deep-teal' : ''}
            data-testid="bookings-tab"
          >
            <Calendar className="w-4 h-4 mr-2" />
            Bookings ({bookings.length})
          </Button>
        </div>

        {/* Availability Tab */}
        {activeTab === 'availability' && (
          <Card data-testid="availability-settings">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-cracked-deep-teal">
                <Clock className="w-5 h-5" />
                Availability Settings
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Slot Duration */}
              <div className="space-y-2">
                <Label className="text-cracked-deep-teal">Slot Duration (minutes)</Label>
                <Input
                  type="number"
                  min="5"
                  max="120"
                  value={availability.slot_duration}
                  onChange={(e) => setAvailability(prev => ({ ...prev, slot_duration: parseInt(e.target.value) || 10 }))}
                  className="w-32"
                  data-testid="slot-duration-input"
                />
                <p className="text-xs text-cracked-deep-teal/50">Default is 10 minutes. Customize based on your interview needs.</p>
              </div>

              <Separator />

              {/* Weekly Schedule */}
              <div className="space-y-4">
                <Label className="text-cracked-deep-teal">Weekly Schedule</Label>
                <p className="text-xs text-cracked-deep-teal/50 -mt-2">
                  Add multiple time ranges per day to schedule breaks (e.g., lunch). The gaps between ranges will not be bookable.
                </p>
                {DAYS.map((day, index) => {
                  const dayRanges = availability.slots
                    .filter(s => s.day_of_week === index)
                    .sort((a, b) => a.start_time.localeCompare(b.start_time));
                  const isEnabled = dayRanges.length > 0;

                  return (
                    <div key={day} className="flex items-start gap-4 p-3 rounded-lg bg-slate-50" data-testid={`day-${index}`}>
                      <Switch
                        checked={isEnabled}
                        onCheckedChange={() => toggleDayAvailability(index)}
                        className="mt-2"
                        data-testid={`day-${index}-switch`}
                      />
                      <span className={`w-24 font-medium mt-2 ${isEnabled ? 'text-cracked-deep-teal' : 'text-cracked-deep-teal/40'}`}>
                        {day}
                      </span>
                      {isEnabled && (
                        <div className="flex-1 space-y-2">
                          {dayRanges.map((range, rIdx) => (
                            <div key={rIdx} className="flex items-center gap-2" data-testid={`day-${index}-range-${rIdx}`}>
                              <Input
                                type="time"
                                value={range.start_time}
                                onChange={(e) => updateRange(index, rIdx, 'start_time', e.target.value)}
                                className="w-32"
                                data-testid={`day-${index}-range-${rIdx}-start`}
                              />
                              <span className="text-cracked-deep-teal/50">to</span>
                              <Input
                                type="time"
                                value={range.end_time}
                                onChange={(e) => updateRange(index, rIdx, 'end_time', e.target.value)}
                                className="w-32"
                                data-testid={`day-${index}-range-${rIdx}-end`}
                              />
                              {dayRanges.length > 1 && (
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => removeRange(index, rIdx)}
                                  className="text-red-500 hover:text-red-700 hover:bg-red-50"
                                  data-testid={`day-${index}-range-${rIdx}-remove`}
                                  title="Remove this range"
                                >
                                  <X className="w-4 h-4" />
                                </Button>
                              )}
                            </div>
                          ))}
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => addRangeToDay(index)}
                            className="text-cracked-light-teal hover:bg-cracked-light-teal/10 -ml-2"
                            data-testid={`day-${index}-add-range`}
                          >
                            <Plus className="w-4 h-4 mr-1" />
                            Add another range
                          </Button>
                          <div className="pt-1">
                            <CopyToPopover
                              sourceDayIndex={index}
                              onCopy={copyDayTo}
                            />
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              <Button 
                onClick={saveAvailability} 
                disabled={saving}
                className="bg-cracked-light-teal hover:bg-cracked-deep-teal text-white"
                data-testid="save-availability-btn"
              >
                {saving ? <div className="spinner mr-2" /> : null}
                Save Availability
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Bookings Tab */}
        {activeTab === 'bookings' && (
          <Card data-testid="bookings-list">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-cracked-deep-teal">
                <Calendar className="w-5 h-5" />
                Your Bookings
              </CardTitle>
            </CardHeader>
            <CardContent>
              {bookings.length === 0 ? (
                <div className="text-center py-12 text-cracked-deep-teal/50">
                  <Calendar className="w-12 h-12 mx-auto mb-4 opacity-30" />
                  <p>No bookings yet</p>
                  <p className="text-sm">Share your booking link to start receiving bookings</p>
                </div>
              ) : (
                <ScrollArea className="h-[500px]">
                  <div className="space-y-4">
                    {bookings.map((booking) => {
                      const start = formatDateTime(booking.slot_start);
                      const end = formatDateTime(booking.slot_end);
                      const isPast = new Date(booking.slot_start) < new Date();

                      return (
                        <div 
                          key={booking.id} 
                          className={`p-4 rounded-xl border ${isPast ? 'bg-slate-50 border-slate-200' : 'bg-white border-cracked-light-teal/20'}`}
                          data-testid={`booking-${booking.id}`}
                        >
                          <div className="flex items-start justify-between gap-4">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-2">
                                <span className={`text-sm font-medium ${isPast ? 'text-cracked-deep-teal/50' : 'text-cracked-light-teal'}`}>
                                  {start.date}
                                </span>
                                <ChevronRight className="w-4 h-4 text-cracked-deep-teal/30" />
                                <span className={`text-sm ${isPast ? 'text-cracked-deep-teal/50' : 'text-cracked-deep-teal'}`}>
                                  {start.time} - {end.time}
                                </span>
                              </div>
                              <p className={`font-medium ${isPast ? 'text-cracked-deep-teal/50' : 'text-cracked-deep-teal'}`}>
                                Booked by: {booking.counsellor_name}
                              </p>
                              <p className="text-sm text-cracked-deep-teal/60">{booking.counsellor_email}</p>
                              {booking.additional_emails?.length > 0 && (
                                <p className="text-sm text-cracked-deep-teal/50 mt-1">
                                  + {booking.additional_emails.join(', ')}
                                </p>
                              )}
                            </div>
                            <div className="flex items-center gap-2">
                              {booking.meet_link && (
                                <a
                                  href={booking.meet_link}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="p-2 rounded-lg bg-cracked-light-teal/10 text-cracked-light-teal hover:bg-cracked-light-teal/20 transition-colors"
                                  data-testid={`meet-link-${booking.id}`}
                                >
                                  <Video className="w-4 h-4" />
                                </a>
                              )}
                              {!isPast && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => cancelBooking(booking.id)}
                                  className="text-red-500 hover:text-red-700 hover:bg-red-50"
                                  data-testid={`cancel-booking-${booking.id}`}
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </ScrollArea>
              )}
            </CardContent>
          </Card>
        )}
      </main>
    </div>
  );
}
